import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMediaQuery } from 'react-responsive';

import { Drawer } from 'honeydew/Drawer';
import Modal from 'honeydew/Modal';
import useTailwindTheme from 'hooks/useTailwindTheme';

/**
 * DrawerModal component that renders as a Drawer on mobile and Modal on desktop
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to be displayed
 * @param {boolean} [props.disableClose=false] - Whether to disable closing the modal
 * @param {Function} props.onClose - Function called when the modal should be closed
 * @param {Object} [props.drawerProps={}] - Additional props to pass to the Drawer component
 * @param {Object} [props.modalProps={}] - Additional props to pass to the Modal component
 * @returns {JSX.Element} DrawerModal component
 */
function DrawerModal({ children, disableClose = false, drawerProps = {}, modalProps = {}, onClose, ...rest }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const handleClose = () => {
        if (!disableClose) {
            onClose();
        }
    };

    if (mobile) {
        return (
            <Drawer anchor="bottom" open toggleDrawer={handleClose} {...drawerProps}>
                <div className="relative mb-10">
                    <XMarkIcon
                        className="absolute right-10 top-10 size-25 cursor-pointer md:hidden"
                        onClick={handleClose}
                    />
                </div>
                <div className="p-20">{children}</div>
            </Drawer>
        );
    }

    return (
        <Modal
            open
            onClose={handleClose}
            styles={{ contentRoot: 'md:max-w-[500px] lg:max-w-[600px]' }}
            {...modalProps}
            {...rest}
        >
            {children}
        </Modal>
    );
}

export default DrawerModal;
